import { Box } from '@mui/material';
import { useFormik } from 'formik';
import React, { FC } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { requiredDocumentsValidationSchema } from '../../../../domain/document-collection';
import { PortalType } from '../../../../domain/portal';
import { CollectDocumentsNextAction } from '../../../../domain/qualification';
import { YouCanComeBackNote } from '../../../../general/components/footers/you-can-come-back-note';
import { WizardPage } from '../../../../general/components/wizard-page';
import { RootState } from '../../../../store';
import { SubmitWorkflowWithBack } from '../../../workflow/components/submit-workflow-with-back';
import { DocumentUploadCard } from '../../components/document-upload-card';
import { uploadDocuments } from '../../reducers/document-requirements.reducer';

const selector = ({
  documentRequirements: { requiredDocuments, loading: uploading, error: uploadError },
  nextStep: { loading: appLoading, error: appError },
}: RootState) => ({
  documents: requiredDocuments,
  loading: uploading || appLoading,
  error: uploadError ?? appError,
});

type Props = {
  pdfOnly?: boolean;
  nextAction: CollectDocumentsNextAction;
  onBack?: () => void;
  portalType: PortalType;
};

const DocumentCollectionPage: FC<Props> = ({ pdfOnly = false, nextAction, onBack, portalType }: Props) => {
  const dispatch = useDispatch();
  const { documents, loading, error } = useSelector(selector);

  const formik = useFormik({
    initialValues: {
      documents,
    },
    validationSchema: requiredDocumentsValidationSchema,
    onSubmit: (values) => {
      dispatch(uploadDocuments({ documents: values.documents, nextAction }));
    },
    enableReinitialize: true,
  });

  const subtext =
    portalType === 'verify'
      ? "We'll use this information to verify your company details."
      : 'In order to complete your application, please upload the following:';

  return (
    <WizardPage
      title="We need some documents"
      subtext={subtext}
      error={error}
      footer={<YouCanComeBackNote />}
      onBack={onBack}
    >
      <form onSubmit={formik.handleSubmit}>
        <Box sx={{ display: 'grid', gap: 2 }}>
          {formik.values.documents.map((document, index) => (
            <DocumentUploadCard
              key={`${document.documentName}-${document.entityToken}`}
              documentIndex={index}
              formik={formik}
              pdfOnly={pdfOnly}
              uploadingError={!!error}
            />
          ))}
        </Box>

        <SubmitWorkflowWithBack progress={loading} sx={{ mt: 4 }} type="submit">
          Submit
        </SubmitWorkflowWithBack>
      </form>
    </WizardPage>
  );
};

export { DocumentCollectionPage };
